import React, { useState } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  Form,
  InputGroup,
  Row,
  Col,
  Card,
  Pagination,
} from 'react-bootstrap'
import {
  FaArrowsAltV,
  FaLongArrowAltUp,
  FaLongArrowAltDown,
} from 'react-icons/fa'

export type DataTableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData, any>[]
  renderSubComponent?: (row: any) => React.ReactNode
}

export type TableColumnMeta = {
  align?: 'left' | 'center' | 'right'
}

export type GenericColumnDef<TData> = ColumnDef<TData, any> & {
  meta?: TableColumnMeta
}

export function DataTable<TData>({
  data,
  columns,
  renderSubComponent,
}: DataTableProps<TData>) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<any>([])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 30 })

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!renderSubComponent,
  })

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        {/* 🔎 Global Search */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>🔍</InputGroup.Text>
              <Form.Control
                placeholder="Search all columns..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>
        {/* 📊 Table */}
        <Table
          striped
          bordered={false}
          hover
          responsive
          className="align-middle data-table shadow-sm"
        >
          <thead className="table-light">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      cursor: header.column.getCanSort()
                        ? 'pointer'
                        : 'default',
                      whiteSpace: 'nowrap',
                      width: header.column.getSize(),
                    }}
                  >
                    {/* Column title */}
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {/* Sorting icon */}
                    {header.column.getCanSort() && (
                      <span className="ms-1">
                        {{
                          asc: <FaLongArrowAltUp className="text-primary" />, // 🔼 Blue up arrow
                          desc: <FaLongArrowAltDown className="text-primary" />, // 🔽 Blue down arrow
                        }[header.column.getIsSorted() as string] ?? (
                          <FaArrowsAltV className="text-primary" /> // ↕ Blue thin arrows
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}

            {/* 🔽 Column Filters */}
            <tr>
              {table.getHeaderGroups()[0].headers.map((header) => (
                <th key={header.id}>
                  {header.column.getCanFilter() ? (
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder={`Filter...`}
                      value={(header.column.getFilterValue() as string) ?? ''}
                      onChange={(e) =>
                        header.column.setFilterValue(e.target.value)
                      }
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={row.getToggleExpandedHandler()}
                    style={{
                      cursor: renderSubComponent ? 'pointer' : 'default',
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={
                          cell.column.columnDef.meta?.align === 'right'
                            ? 'text-end'
                            : ''
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>

                  {row.getIsExpanded() && renderSubComponent && (
                    <tr>
                      <td colSpan={columns.length}>
                        {renderSubComponent(row)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-muted py-4"
                >
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* 📑 Pagination */}
        <Row className="align-items-center">
          <Col>
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              {Array.from({ length: table.getPageCount() }).map((_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === table.getState().pagination.pageIndex}
                  onClick={() => table.setPageIndex(i)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
            </Pagination>
          </Col>
          <Col className="text-end">
            <Form.Select
              size="sm"
              style={{ width: 'auto', display: 'inline-block' }}
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
            >
              {[30, 50, 100, 150].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}
